// TODO: Abstract away navigation items

import {
  AdminPanelSettings,
  AssignmentTurnedIn,
  ConfirmationNumber,
  Dashboard,
  DirectionsRun,
  EventNote,
  ExpandLess,
  ExpandMore,
  Info,
  Inventory2,
  Language,
  LiveTv,
  Logout,
  PendingActions,
  Person,
  Savings,
  ShoppingCart,
  Translate,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import UnfoldMoreDoubleIcon from '@mui/icons-material/UnfoldMoreDouble';
import {
  AppBar,
  Avatar,
  Box,
  Collapse,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLongPress } from 'use-long-press';

import { useAuth } from '@/contexts/AuthContext';
import { useEvent } from '@/contexts/EventsContext';

const MAX_LOGO_WIDTH = 128;
const infoItems = [
  { text: 'nav.activities', href: '/events/guides/activities' },
  { text: 'nav.marketplace', href: '/events/guides/marketplace' },
  { text: 'nav.attendance', href: '/events/guides/attendance' },
  { text: 'nav.submission', href: '/events/guides/submission' },
  { text: 'nav.volunteer', href: '/events/guides/volunteering' },
  {
    text: 'nav.volunteerComplimentaryPass',
    href: '/events/guides/volunteering/complimentary-pass',
  },
  { text: 'nav.vendor', href: '/events/guides/vendor' },
  { text: 'nav.rules', href: '/events/guides/rules' },
  { text: 'nav.privacy-policy', href: '/policies/privacy' },
  { text: 'nav.terms-of-use', href: '/policies/terms-of-use' },
  { text: 'nav.refund-policy', href: '/policies/refund' },
];

function NavLinkItem({ href, children }) {
  const router = useRouter();
  const active = href === router.route;

  return (
    <Typography
      variant="h6"
      component="a"
      href={href}
      className="font-bebas hover:text-blue-400"
      sx={[
        {
          my: 2,
          display: 'block',
          borderBottom: '2px solid',
          textDecoration: 'none',
          transition: 'all .15s ease',
        },
        !active && { borderBottomColor: 'transparent' },
      ]}
    >
      {children}
    </Typography>
  );
}

NavLinkItem.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
};

function SubNav({ icon, primary, items }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleClick = () => setOpen(!open);

  // Function to handle closing the sub-menu if the click is outside of the component
  const handleClickOutside = event => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  // Listen for clicks outside of the component to close the sub-menu
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef}>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={primary} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse unmountOnExit in={open} timeout="auto">
        <List disablePadding component="div">
          {items.map(item => (
            <ListItemButton
              key={item.href}
              component="a"
              href={item.href}
              selected={item.href === router.route}
              sx={{ paddingLeft: 4 }}
            >
              <ListItemText
                inset
                sx={{ '& .MuiListItemText-primary': { textDecoration: 'none' } }}
                primary={t(item.text)}
              />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </div>
  );
}

SubNav.propTypes = {
  icon: PropTypes.element.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ).isRequired,
  primary: PropTypes.string.isRequired,
};

function MobileNavigation() {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    active: activeEvent,
    open: { 0: openEvent },
    areDonationsOpen,
  } = useEvent();
  const [isOpen, setIsOpen] = useState(false);
  const handleOpenNavMenu = () => setIsOpen(true);
  const handleCloseNavMenu = () => setIsOpen(false);

  return (
    <Box sx={{ display: { xs: 'flex', md: 'none' }, width: `calc(50% - ${MAX_LOGO_WIDTH / 2}px)` }}>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleOpenNavMenu}
        color="inherit"
      >
        <MenuIcon />
      </IconButton>
      <Drawer anchor="left" open={isOpen} onClose={handleCloseNavMenu}>
        <List sx={{ minWidth: '18rem' }}>
          <ListItemButton component="a" href="/" selected={router.route === '/'}>
            <ListItemIcon>
              <Image src="/imgs/header_logo.png" width={32} height={16} alt="Home" />
            </ListItemIcon>
            <ListItemText primary={t('nav.home')} />
          </ListItemButton>

          <ListItemButton component="a" href={process.env.NEXT_PUBLIC_SHOP_URL}>
            <ListItemIcon>
              <ShoppingCart />
            </ListItemIcon>
            <ListItemText primary="Shop" />
          </ListItemButton>

          <ListItemButton
            component="a"
            href="https://discord.noreset.tv"
            target="_blank"
            rel="noreferrer"
          >
            <ListItemIcon>
              <Image src="/imgs/discord.svg" width={24} height={24} alt="Discord" />
            </ListItemIcon>
            <ListItemText primary="Discord" />
          </ListItemButton>

          {openEvent ? (
            <>
              <ListItemButton
                component="a"
                href="/events/submit"
                selected={router.route === '/events/submit'}
              >
                <ListItemIcon>
                  <DirectionsRun />
                </ListItemIcon>
                <ListItemText primary={t('nav.submit')} />
              </ListItemButton>
              <ListItemButton
                component="a"
                href="/events/submitted"
                selected={router.route === '/events/submitted'}
              >
                <ListItemIcon>
                  <PendingActions />
                </ListItemIcon>
                <ListItemText primary={t('nav.submitted')} />
              </ListItemButton>
            </>
          ) : (
            activeEvent?.submissionsStartAt && (
              <ListItemButton
                component="a"
                href="/schedule"
                selected={router.route === '/schedule'}
              >
                <ListItemIcon>
                  <EventNote />
                </ListItemIcon>
                <ListItemText primary={t('nav.schedule')} />
              </ListItemButton>
            )
          )}

          {activeEvent && areDonationsOpen && (
            <ListItemButton
              component="a"
              href="/donations"
              selected={router.route === '/donations'}
            >
              <ListItemIcon>
                <Savings />
              </ListItemIcon>
              <ListItemText primary={t('nav.donations')} />
            </ListItemButton>
          )}

          <SubNav primary="Info" icon={<Info />} items={infoItems} />
        </List>
      </Drawer>
    </Box>
  );
}

function DesktopNavigation() {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    active: activeEvent,
    open: { 0: openEvent },
    areDonationsOpen,
  } = useEvent();
  const [anchorElementInfo, setAnchorElementInfo] = useState(null);
  const handleOpenInfoMenu = event => setAnchorElementInfo(event.currentTarget);
  const handleCloseInfoMenu = () => setAnchorElementInfo(null);

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, gap: 4, marginLeft: 4 }}>
      <NavLinkItem href="/">{t('nav.home')}</NavLinkItem>

      <Typography
        variant="h6"
        component="a"
        href={process.env.NEXT_PUBLIC_SHOP_URL}
        target="_blank"
        className="font-bebas hover:text-blue-400"
        sx={{ display: 'block', my: 2 }}
      >
        {t('nav.shop')}
      </Typography>

      {openEvent ? (
        /* SUBMISSIONS OPEN */
        <>
          <NavLinkItem href="/events/submit">{t('nav.submit')}</NavLinkItem>
          <NavLinkItem href="/events/submitted">{t('nav.submitted')}</NavLinkItem>
        </>
      ) : (
        /* SCHEDULE OPEN */
        activeEvent?.submissionsStartAt && (
          <NavLinkItem href="/schedule">{t('nav.schedule')}</NavLinkItem>
        )
      )}

      {activeEvent && areDonationsOpen && (
        <NavLinkItem href="/donations">{t('nav.donations')}</NavLinkItem>
      )}

      <Typography
        variant="h6"
        component="a"
        href="https://discord.noreset.tv/"
        target="_blank"
        rel="noreferrer"
        className="font-bebas hover:text-blue-400"
        sx={{ display: 'block', my: 2 }}
      >
        Discord
      </Typography>

      <Typography
        component="button"
        variant="h6"
        onClick={handleOpenInfoMenu}
        className="font-bebas hover:text-blue-400 cursor-pointer"
        sx={{ display: 'block', my: 2 }}
      >
        Info{anchorElementInfo ? <ExpandLess /> : <ExpandMore />}
      </Typography>
      <Menu
        keepMounted
        sx={{ marginTop: '45px' }}
        id="menu-appbar-info"
        anchorEl={anchorElementInfo}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={Boolean(anchorElementInfo)}
        onClose={handleCloseInfoMenu}
      >
        {infoItems.map(setting => (
          <MenuItem
            key={setting.href}
            component="a"
            href={setting.href}
            onClick={handleCloseInfoMenu}
            selected={router.route === setting.href}
          >
            <Typography>{t(setting.text)}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function ProfileNavigation({ onNavBarHide }) {
  const { user, groups } = useAuth();
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const [anchorElementUser, setAnchorElementUser] = useState(null);
  const handleLanguageMode = () => i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');
  const handleOpenUserMenu = event => setAnchorElementUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElementUser(null);
  const longPress = useLongPress(
    onNavBarHide,
    { threshold: 1200 }, // 🤫 this is a secret !
  );

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Typography
        variant="h6"
        component="button"
        sx={{ marginRight: 2 }}
        onClick={handleLanguageMode}
        className="font-bebas hover:text-blue-400"
      >
        {i18n.language === 'en' ? 'Français' : 'English'}
      </Typography>

      {user ? (
        <>
          <Tooltip title="Open settings">
            <IconButton {...longPress()} onClick={handleOpenUserMenu} sx={{ padding: 0 }}>
              <Avatar alt={user.username[0]} src={user.avatarUrl} />
            </IconButton>
          </Tooltip>
          <Menu
            keepMounted
            sx={{ marginTop: '45px' }}
            id="menu-appbar"
            anchorEl={anchorElementUser}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElementUser)}
            onClose={handleCloseUserMenu}
          >
            <MenuItem onClick={handleLanguageMode}>
              <ListItemIcon>
                <Language />
              </ListItemIcon>
              <Typography sx={{ textAlign: 'center' }}>
                {i18n.language === 'en' ? 'EN → FR' : 'FR → EN'}
              </Typography>
            </MenuItem>

            {user?.isStaff && (
              <MenuItem component="a" href="/api/v2/admin/" onClick={handleCloseUserMenu}>
                <ListItemIcon>
                  <AdminPanelSettings />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.admin')}</Typography>
              </MenuItem>
            )}

            {groups.includes('Translator') && (
              <MenuItem
                component="a"
                href="/api/v2/admin/api/translation/"
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <Translate />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.translations')}</Typography>
              </MenuItem>
            )}

            {groups.includes('OES') && (
              <MenuItem component="a" href="/oes" target="_blank" onClick={handleCloseUserMenu}>
                <ListItemIcon>
                  <Dashboard />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>OES</Typography>
              </MenuItem>
            )}

            {groups.includes('Inventory') && (
              <MenuItem
                component="a"
                href="/inventory"
                selected={router.route === '/inventory'}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <Inventory2 />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.inventory')}</Typography>
              </MenuItem>
            )}

            {groups.includes('Host') && (
              <MenuItem
                component="a"
                href="/host"
                selected={router.route === '/host'}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <LiveTv />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.host')}</Typography>
              </MenuItem>
            )}

            {groups.includes('Admissions') && (
              <MenuItem
                component="a"
                href="/admissions"
                selected={router.route === '/admissions'}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <ConfirmationNumber />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.admissions')}</Typography>
              </MenuItem>
            )}

            {groups.includes('Submissions') && (
              <MenuItem
                component="a"
                href="/events/submissions"
                selected={router.route === '/events/submissions'}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <AssignmentTurnedIn />
                </ListItemIcon>
                <Typography sx={{ textAlign: 'center' }}>{t('nav.submissions')}</Typography>
              </MenuItem>
            )}

            <MenuItem
              component="a"
              href="/profile"
              selected={router.route === '/profile'}
              onClick={handleCloseUserMenu}
            >
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <Typography sx={{ textAlign: 'center' }}>{t('profile.title')}</Typography>
            </MenuItem>
            <MenuItem component="a" href="/accounts/logout/" onClick={handleCloseUserMenu}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <Typography sx={{ textAlign: 'center' }}>{t('nav.logout')}</Typography>
            </MenuItem>
          </Menu>
        </>
      ) : (
        <Typography
          {...longPress()}
          variant="h6"
          component="a"
          href="/accounts/login/"
          className="font-bebas hover:text-blue-400"
          display={{ xs: 'none', md: 'inline-block' }}
        >
          {t('nav.login')}
        </Typography>
      )}
    </Box>
  );
}
ProfileNavigation.propTypes = {
  onNavBarHide: PropTypes.func.isRequired,
};

export default function ResponsiveAppBar() {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(localStorage.getItem('navBarOpen') !== 'false');
  const toggleOpen = () => {
    localStorage.setItem('navBarOpen', (!open).toString());
    setOpen(!open);
  };

  return (
    <>
      <Slide mountOnEnter unmountOnExit appear={false} direction="down" in={open}>
        <AppBar position="static">
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <MobileNavigation />
              <Link href="/" style={isMobile ? { flex: 1 } : undefined}>
                <Image
                  src="/imgs/header_logo.png"
                  width={MAX_LOGO_WIDTH}
                  height={64}
                  alt="NoReset Logo"
                />
              </Link>
              <DesktopNavigation />
              <ProfileNavigation onNavBarHide={toggleOpen} />
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>
      <Slide appear={false} direction="down" in={!open}>
        <Box
          sx={{
            position: 'fixed',
            top: theme.spacing(2),
            right: '3.4rem', // Based on Host Panel's Twitch chat widget
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="button"
            sx={{ marginRight: 1 }}
            onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en')}
            className="font-bebas hover:text-blue-400"
          >
            {i18n.language === 'en' ? 'Français' : 'English'}
          </Typography>
          <IconButton onClick={toggleOpen}>
            <UnfoldMoreDoubleIcon />
          </IconButton>
        </Box>
      </Slide>
    </>
  );
}
